import { api_configuration } from "../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import { DatastoreProxy } from "../interface/DatastoreProxy";
import NodePathBuilder from "../navigation/NodePathBuilder";
import { CollectionPathBuilder } from "../navigation/createCollectionPathBuilder";
import { createIdPathBuilderFromNode } from "../navigation/createIdPathFromNode";
import * as mapToIdPathBuilder from "../state/mapToIdPathBuilder";
import { IdPathBuilder } from "../navigation/IdPathBuilder";
import * as Text from "../state/Text";
import { registerIdPath, registrationDone } from "../state/registerClientState";
import { entry_reference_definition, descendant_object_path, where_rule_object_path } from "../state/expressions";
import * as step from "../state/step";
import supportsSelfReference from "../state/supportsSelfReference";
import * as referenceResolver from "../state 2 interface/referenceResolver";
import {
	ScalarProvider,
	WeakCache,
	Scalar,
	createVariable,
	createExpressionBuilder,
	DefaultExpressionBuilder,
	lambda,
} from "../../../../../stream";
import { Pending, Unknown, Result, Failed } from "../Status";
import { whereRuleObjectPath, entryReferenceSynchronized } from "../state/expressionSynchronized";
import { getGuiRule } from "../guiModel";
import { toStream } from "../toStream";

export function isMandatory(rule: api_cs.Crules): boolean {
	return rule.key.ref.properties.behaviour.properties.type.switch({
		mandatory: () => true,
		optional: () => getGuiRule(rule.key.ref)?.properties.validate.switch({ no: false, yes: true }) ?? false,
	});
}

export function query_constraint(id_path_builder: IdPathBuilder, path: NodePathBuilder) {
	const stream = createVariable<Result<IdPathBuilder>>(Unknown, true);
	DatastoreProxy.instance
		.collectionQuery({
			"collection path": CollectionPathBuilder.create().build(),
			"context node path": id_path_builder.build(),
			filters: {
				"todo filter": ["no", {}],
				"value filters": {},
			},
			aggregates: {},
			"select entries": [
				"yes",
				{
					properties: {
						properties: {
							["parameter"]: {
								path: path.build(),
								type: ["id path", {}],
							},
						},
					},
					"limit number of entries": 1,
				},
			],
		})
		.then((result) => {
			result.properties.context.switch({
				"permission denied": () => stream.set(Failed),
				"does not exist": () => stream.set(Failed),
				exists: ($) =>
					$.properties.entries
						.cast("yes")
						.properties.entries.properties.has_steps.cast("no")
						.properties.properties.properties.properties.get("parameter")!
						.properties.result.switch({
							"permission denied": () => stream.set(Failed),
							"does not exist": () => stream.set(Failed),
							exists: ($) =>
								stream.set(mapToIdPathBuilder.fromClientReply($.properties.type.cast("id path").properties.id_path)),
						}),
			});
		});
	return stream;
}

export const ruleExpressionBuilder = WeakCache<
	api_app.Crules,
	DefaultExpressionBuilder<api_cs.Crules, Result<api_cs.Cnode>>
>((definition) => {
	const definer = definition.parent.input.definer().variant;
	switch (definition.properties.type.state.name) {
		case "entry reference": {
			const $ = definition.properties.type.state.node;
			const expression = $.properties.expression;
			switch (definer.name) {
				case "text": {
					const self_reference_allowed = supportsSelfReference(expression);
					return lambda((rule: api_cs.Crules) => {
						const text = rule.parent.input.definer().cast("reference").parent;
						const this_node = text.component_root;
						return entry_reference_definition(expression)
							.pipe(step.entry(registrationDone(text, () => Text.value(text))()))
							.pipe(
								step.sync((entry) => {
									const this_entry = text.component_root.input.entity().cast("entry").properties.node;
									if (!self_reference_allowed && entry === this_entry) return Failed;
									return entry;
								})
							)
							.pipe(descendant_object_path(expression.properties.tail))(this_node) as ScalarProvider<
							Result<api_cs.Cnode>
						>;
					});
				}
				case "state": {
					return lambda((rule: api_cs.Crules) =>
						createExpressionBuilder(
							query_constraint(
								createIdPathBuilderFromNode(rule.parent.input.definer().cast("state").properties.node),
								new NodePathBuilder().state_rule(definition.key_value)
							)
						).pipe(step.sync((id_path_builder, destructor) => registerIdPath(id_path_builder, destructor)))()
					);
				}
			}
			break;
		}
		case "node reference": {
			const $ = definition.properties.type.state.node;
			const $type = $.properties.type.state;
			switch ($type.name) {
				case "existence":
					return where_rule_object_path($.properties.object_path) as DefaultExpressionBuilder<
						api_cs.Crules,
						Result<api_cs.Cnode>
					>;
				case "equality": {
					return lambda((rule: api_cs.Crules) =>
						where_rule_object_path($.properties.object_path).pipe(
							step.sync((object) =>
								where_rule_object_path($type.node.properties.node_path).pipe(
									step.sync((other_object) => {
										if (object.is(other_object)) {
											return object as api_cs.Cnode;
										} else {
											return Failed;
										}
									})
								)(rule)
							)
						)(rule)
					) as DefaultExpressionBuilder<api_cs.Crules, Result<api_cs.Cnode>>;
				}
				case "containment": {
					const key_stream = where_rule_object_path($type.node.properties.key_node_path).pipe(
						step.sync((key_node) => {
							const key = (key_node as api_cs.Cnode).input.entity().cast("entry").properties.key.ref;
							return registrationDone(key, () => Text.value(key))();
						})
					);
					return lambda((rule: api_cs.Crules) =>
						where_rule_object_path($.properties.object_path)
							.pipe((col) => col as Result<api_cs.Ccollection>) // just to cast to col
							.pipe(step.entry(key_stream(rule)))(rule)
					);
				}
			}
		}
	}
});

export const node = WeakCache<api_cs.Crules, Scalar<Result<api_cs.Cnode>>>((rule) => {
	const resolve_status = rule.properties.resolve_status;
	resolve_status.properties.status.ensure("is busy resolving", {});
	const stream = createVariable(ruleExpressionBuilder(rule.key.ref)(rule), false);
	stream.subscribe({
		update: (node) => {
			if (resolve_status.destroyed) return;
			switch (node) {
				case Pending:
					resolve_status.properties.status.ensure("is busy resolving", {});
					break;
				case Unknown:
					resolve_status.properties.status.ensure("unresolved", {});
					break;
				case Failed:
					resolve_status.properties.status.ensure("failed to resolve", {});
					break;
				default:
					resolve_status.properties.status.ensure("resolved", {});
			}
		},
	});
	referenceResolver.subscribeToReference(resolve_status);
	rule.onDestroy(() => stream.destroy());
	return stream;
});

export const synchronized = WeakCache<api_cs.Crules, Scalar<Result<"yes">>>((rule) => {
	const definition = rule.key.ref;
	const definer = rule.parent.input.definer().variant;
	switch (
		definer.definition.parent.parent.inferences.property_type().properties.type.state.node.output.value_source().variant
			.name
	) {
		case "derived":
			return "yes";
		case "base":
			switch (definition.properties.type.state.name) {
				case "entry reference": {
					const $ = definition.properties.type.state.node;
					const expression = $.properties.expression;
					switch (definer.name) {
						case "reference":
							return createVariable(entryReferenceSynchronized(definer.definition.parent, expression)(), false);
						case "state":
							return "yes";
					}
					break;
				}
				case "node reference": {
					const $ = definition.properties.type.state.node;
					return createVariable(
						rule.parent.input
							.definer()
							.switch({
								state: ($) => toStream($.properties.node.properties.location),
								reference: ($) => toStream($.component_root.properties.location),
							})
							.stateSwitch({
								local: () => Failed as Scalar<Result<"yes">>,
								remote: () => {
									const $type = $.properties.type.state;
									switch ($type.name) {
										case "existence":
											return whereRuleObjectPath($.properties.object_path)(rule);
										case "equality":
											return whereRuleObjectPath($.properties.object_path)
												.and(createExpressionBuilder(rule).pipe(whereRuleObjectPath($type.node.properties.node_path))())
												.pipe(([left, right]) => {
													if (left === Failed || right === Failed) {
														return Failed;
													} else if (left === Pending || right === Pending) {
														return Pending;
													} else {
														return "yes" as const;
													}
												})(rule);
										case "containment":
											return whereRuleObjectPath($type.node.properties.key_node_path)
												.and(createExpressionBuilder(rule).pipe(whereRuleObjectPath($.properties.object_path))())
												.pipe(([left, right]) => {
													if (left === Failed || right === Failed) {
														return Failed;
													} else if (left === Pending || right === Pending) {
														return Pending;
													} else {
														return "yes" as const;
													}
												})(rule);
									}
								},
							})(),
						false
					);
					break;
				}
			}
	}
});
